import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { journals } from "../lib/api";
import useSubscriptionStatus from "../lib/useSubscriptionStatus";
import { toast } from "sonner";
import PaywallModal from "../components/PaywallModal";
import {
  CalendarDays,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Check,
  Loader2,
} from "lucide-react";

const PROMPTS = [
  { key: "stood_out", label: "What stood out to me this week?" },
  { key: "gratitude", label: "What am I grateful for?" },
  { key: "difficult", label: "What felt difficult or unresolved?" },
  { key: "growth", label: "Where did I notice growth, clarity, or God at work?" },
  { key: "carry_forward", label: "What do I want to carry into next week?" },
];

const SOURCE_BADGES = {
  manual: { label: "Personal", tone: "bg-white text-[#4E5A4C] border-[#DCE5DB]" },
  coach: { label: "From Coach", tone: "bg-[#EEF3EC] text-[#4F6F52] border-[#dce5db]" },
  guidance: { label: "From Guidance", tone: "bg-[#E1ECDD] text-[#4F6F52] border-[#d3e0d0]" },
};

function useHasWriteAccess() {
  return useSubscriptionStatus().isPaid;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function WeekBadge({ start, end }) {
  const fmt = (s) => {
    try {
      return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return s;
    }
  };
  return (
    <span
      data-testid="weekly-date-range"
      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4F6F52] bg-[#E1ECDD] border border-[#B7CCB1] rounded-full px-3 py-1"
    >
      <CalendarDays size={12} strokeWidth={2.2} /> {fmt(start)} — {fmt(end)}
    </span>
  );
}

function EntryRow({ entry }) {
  const [open, setOpen] = useState(false);
  const badge = SOURCE_BADGES[entry.source] || SOURCE_BADGES.manual;
  return (
    <div
      data-testid={`weekly-entry-${entry.id}`}
      className="rounded-2xl border border-[#DCE5DB] bg-white overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`weekly-entry-toggle-${entry.id}`}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#EEF3EC]/50 transition"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-[11px] text-[#6B6B6B]">{formatDate(entry.created_at)}</span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-medium ${badge.tone}`}
            >
              {badge.label}
            </span>
          </div>
          <div className="text-[14.5px] text-[#2E2E2E] font-medium truncate">
            {entry.title || entry.content?.slice(0, 60) || "Untitled entry"}
          </div>
        </div>
        {open ? (
          <ChevronUp size={16} className="text-[#6B6B6B] shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-[#6B6B6B] shrink-0" />
        )}
      </button>
      {open && (
        <div
          data-testid={`weekly-entry-body-${entry.id}`}
          className="px-4 pb-4 pt-1 text-[14.5px] leading-[1.75] text-[#2E2E2E] whitespace-pre-wrap border-t border-[#EEF3EC]"
        >
          {entry.content}
        </div>
      )}
    </div>
  );
}

export default function WeeklyReflectionPage() {
  const navigate = useNavigate();
  const canWrite = useHasWriteAccess();
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState({ start: null, end: null });
  const [existing, setExisting] = useState(null);
  const [entries, setEntries] = useState([]);
  const [answers, setAnswers] = useState({
    stood_out: "",
    gratitude: "",
    difficult: "",
    growth: "",
    carry_forward: "",
  });
  const [ai, setAi] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const bundle = await journals.weekly.current();
        setWeek({ start: bundle.week_start, end: bundle.week_end });
        setEntries(bundle.entries || []);
        if (bundle.existing) {
          setExisting(bundle.existing);
          const r = bundle.existing.reflection || {};
          setAnswers({
            stood_out: r.stood_out || "",
            gratitude: r.gratitude || "",
            difficult: r.difficult || "",
            growth: r.growth || "",
            carry_forward: r.carry_forward || "",
          });
          setAi(r.lifeguide_reflection || "");
        }
      } catch {
        toast.error("Couldn't load this week");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const anyAnswered = useMemo(
    () => Object.values(answers).some((v) => (v || "").trim()) || (ai || "").trim(),
    [answers, ai],
  );

  const generateAI = async () => {
    if (!canWrite) return setPaywall(true);
    setAiBusy(true);
    try {
      const res = await journals.weekly.generate();
      setAi(res.text || "");
      toast.success("Reflection drafted — edit it however you like");
    } catch (err) {
      if (err?.response?.status === 402) setPaywall(true);
      else toast.error(err?.response?.data?.detail || "Couldn't draft a reflection");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!canWrite) return setPaywall(true);
    if (!anyAnswered) {
      toast.error("Answer at least one prompt or add a reflection before saving.");
      return;
    }
    setSaving(true);
    const reflection = { ...answers };
    if (ai.trim()) reflection.lifeguide_reflection = ai.trim();
    try {
      if (existing) {
        const updated = await journals.update(existing.id, {
          title: existing.title || `Week of ${week.start}`,
          reflection,
        });
        setExisting(updated);
        toast.success("Weekly Reflection updated");
      } else {
        const created = await journals.create({
          source: "weekly_reflection",
          title: `Week of ${week.start}`,
          week_start: week.start,
          week_end: week.end,
          reflection,
        });
        setExisting(created);
        toast.success("Weekly Reflection saved");
      }
      navigate("/journal");
    } catch (err) {
      if (err?.response?.status === 402) setPaywall(true);
      else toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-40 shimmer rounded-2xl" />
        <div className="h-24 shimmer rounded-3xl" />
        <div className="h-40 shimmer rounded-3xl" />
        <div className="h-40 shimmer rounded-3xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="weekly-reflection-page">
      <div className="fade-in-up">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl text-[#2E2E2E]">Weekly Reflection</h1>
            <p className="text-sm text-[#6B6B6B] mt-1.5 leading-relaxed">
              Look back. Notice what mattered. Carry wisdom forward.
            </p>
          </div>
          <WeekBadge start={week.start} end={week.end} />
        </div>
        <div
          data-testid="weekly-explanation"
          className="mt-4 rounded-2xl border border-[#DCE5DB] bg-white p-4 leading-relaxed"
        >
          <p className="font-medium text-[15px] text-[#2E2E2E]">
            Reflect on your own, or let LifeGuide help.
          </p>
          <p className="text-sm text-[#4E5A4C] mt-1.5">
            Answer any questions you'd like, or choose <span className="font-semibold text-[#3F5A42]">Help me reflect</span> and LifeGuide will use this week's journal entries to help you look back and find what matters.
          </p>
        </div>
      </div>

      {/* From your week */}
      <section className="fade-in-up">
        <h2 className="font-display text-lg text-[#2E2E2E] mb-3">From your week</h2>
        {entries.length === 0 ? (
          <div
            data-testid="weekly-entries-empty"
            className="rounded-3xl border border-dashed border-[#DCE5DB] bg-white p-6 text-center text-sm text-[#6B6B6B] leading-relaxed"
          >
            No Journal entries this week yet. That's okay — you can still reflect below.
          </div>
        ) : (
          <div className="space-y-2" data-testid="weekly-entries-list">
            {entries.map((e) => (
              <EntryRow key={e.id} entry={e} />
            ))}
          </div>
        )}
      </section>

      {/* Prompts */}
      <section className="space-y-4 fade-in-up" data-testid="weekly-prompts">
        {PROMPTS.map((p) => (
          <div
            key={p.key}
            className="rounded-3xl border border-[#DCE5DB] bg-white p-5 shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
          >
            <label
              htmlFor={`prompt-${p.key}`}
              className="block text-[14.5px] font-medium text-[#2E2E2E] mb-2"
            >
              {p.label}
            </label>
            <textarea
              id={`prompt-${p.key}`}
              data-testid={`weekly-prompt-${p.key}`}
              rows={3}
              value={answers[p.key]}
              onChange={(e) => setAnswers((a) => ({ ...a, [p.key]: e.target.value }))}
              placeholder="Take a moment..."
              className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 text-[15px] leading-[1.75] text-[#2E2E2E] focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition resize-none"
            />
          </div>
        ))}
      </section>

      {/* AI reflection */}
      <section className="fade-in-up">
        {!ai ? (
          <button
            onClick={generateAI}
            disabled={aiBusy}
            data-testid="weekly-help-me-reflect"
            className="w-full sm:w-auto inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-[#E1ECDD] hover:bg-[#D0DFCB] text-[#3F5A42] border border-[#B7CCB1] transition disabled:opacity-60"
          >
            {aiBusy ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Reflecting...
              </>
            ) : (
              <>
                <Sparkles size={14} strokeWidth={2.2} /> Help me reflect
              </>
            )}
          </button>
        ) : (
          <div
            data-testid="weekly-lifeguide-reflection"
            className="rounded-3xl border border-[#B7CCB1] bg-[#EEF3EC] p-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)]"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-[#4F6F52]" strokeWidth={2.2} />
              <h3 className="font-display text-lg text-[#2E2E2E]">LifeGuide Reflection</h3>
            </div>
            <p className="text-xs text-[#6B6B6B] mb-3 leading-relaxed">
              Based only on what you chose to save in your Journal. Edit however you like before saving.
            </p>
            <textarea
              data-testid="weekly-ai-textarea"
              rows={7}
              value={ai}
              onChange={(e) => setAi(e.target.value)}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 text-[15px] leading-[1.8] text-[#2E2E2E] focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition resize-none"
            />
            <div className="flex gap-2 justify-between mt-3 flex-wrap">
              <button
                onClick={() => setAi("")}
                data-testid="weekly-ai-clear"
                className="text-sm text-[#6B6B6B] hover:text-[#2E2E2E] rounded-full px-3 py-1.5 hover:bg-white/70 transition"
              >
                Remove
              </button>
              <button
                onClick={generateAI}
                disabled={aiBusy}
                data-testid="weekly-ai-regenerate"
                className="text-sm font-medium text-[#3F5A42] hover:text-[#2E4531] inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 hover:bg-white/70 transition disabled:opacity-60"
              >
                {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} strokeWidth={2.2} />}
                Draft again
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Save */}
      <div className="sticky bottom-24 sm:static flex justify-end pt-2">
        <button
          onClick={save}
          disabled={saving || !anyAnswered}
          data-testid="weekly-save-btn"
          className="bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-50 text-white rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 shadow-[0_4px_14px_rgba(79,111,82,0.22)] transition"
        >
          <Check size={14} strokeWidth={2.4} />
          {saving ? "Saving..." : existing ? "Update Weekly Reflection" : "Save Weekly Reflection"}
        </button>
      </div>

      <PaywallModal
        open={paywall}
        reason="Your LifeGuide journey is saved. Upgrade to LifeGuide Pro to keep writing your Weekly Reflection."
        onClose={() => setPaywall(false)}
      />
    </div>
  );
}
